import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Command, CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { FileText, ShoppingCart, PackageCheck, Building2, Package } from "lucide-react";

export default function GlobalSearch({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ indents: [], pos: [], grns: [], vendors: [], materials: [] });
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  useEffect(() => {
    if (!q || q.length < 2) { setRes({ indents: [], pos: [], grns: [], vendors: [], materials: [] }); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setRes(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => {
    setOpen(false);
    setQ("");
    nav(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search indents, POs, GRNs, vendors, materials…"
        value={q}
        onValueChange={setQ}
        data-testid="global-search-input"
      />
      <CommandList>
        <CommandEmpty>No results. Type at least 2 characters.</CommandEmpty>
        {res.indents.length > 0 && (
          <CommandGroup heading="Indents">
            {res.indents.map((i) => (
              <CommandItem key={i.id} onSelect={() => go(`/indents/${i.id}`)}>
                <FileText size={14} className="mr-2" />
                <span className="font-mono text-xs">{i.indent_no}</span>
                <span className="ml-2 text-muted-foreground">{i.project_name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res.pos.length > 0 && (
          <CommandGroup heading="Purchase Orders">
            {res.pos.map((p) => (
              <CommandItem key={p.id} onSelect={() => go(`/pos/${p.id}`)}>
                <ShoppingCart size={14} className="mr-2" />
                <span className="font-mono text-xs">{p.po_number || p.draft_po_id}</span>
                <span className="ml-2 text-muted-foreground">{p.vendor_name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res.grns.length > 0 && (
          <CommandGroup heading="GRNs">
            {res.grns.map((g) => (
              <CommandItem key={g.id} onSelect={() => go(`/grns/${g.id}`)}>
                <PackageCheck size={14} className="mr-2" />
                <span className="font-mono text-xs">{g.grn_no}</span>
                <span className="ml-2 text-muted-foreground">{g.po_number}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res.vendors.length > 0 && (
          <CommandGroup heading="Vendors">
            {res.vendors.map((v) => (
              <CommandItem key={v.id} onSelect={() => go("/masters?type=vendors")}>
                <Building2 size={14} className="mr-2" />
                <span>{v.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res.materials.length > 0 && (
          <CommandGroup heading="Materials">
            {res.materials.map((m) => (
              <CommandItem key={m.id} onSelect={() => go("/masters?type=materials")}>
                <Package size={14} className="mr-2" />
                <span>{m.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
